import {useParams, Link} from '@remix-run/react';
import {useTranslation} from 'react-i18next';
import {twMerge} from 'tailwind-merge';
import {
  BLOG_SUMMARY_IMAGE_HEIGHT,
  BLOG_SUMMARY_IMAGE_WIDTH,
} from '../constants';
import {BlogSummary} from '../types/BlogSummary';
import {Image} from '~/features/shared';

export function Blogs({
  data,
  size,
  className,
}: {
  data: BlogSummary[];
  size?: 'small' | 'large';
  className?: string;
}) {
  return (
    <div
      className={twMerge(
        'grid gap-6 md:grid-cols-2 lg:grid-cols-3 place-items-start',
        className
      )}
    >
      {data.map((e) => (
        <Blog key={e.handle} item={e} size={size} />
      ))}
    </div>
  );
}

function Blog({item, size}: {item: BlogSummary; size?: 'small' | 'large'}) {
  const params = useParams();
  const {t} = useTranslation();

  const category = item.category;

  if (!category) {
    return null;
  }

  const baseUrl = `/blogs/${category}`;
  const url = `${baseUrl}/${item.handle}`;

  const from = params.category ? 'category' : 'blogs';
  const state = {
    from,
  };

  const s = size ? size : item.size;

  return (
    <article
      className={twMerge(
        'bg-white rounded-lg flex flex-col p-4',
        s === 'large' && 'col-span-1 lg:col-span-2'
      )}
    >
      <Link
        to={url}
        state={state}
        className="overflow-hidden relative mb-4 w-full bg-gray-500 rounded-lg h-[15rem]"
      >
        <Image
          className="object-cover w-full h-full"
          height={BLOG_SUMMARY_IMAGE_HEIGHT}
          width={BLOG_SUMMARY_IMAGE_WIDTH}
          image={item.image}
        />
      </Link>

      <div className="flex flex-col gap-2 items-start">
        <p className="text-sm font-medium tracking-widest uppercase text-blue-dark decoration-blue-dark">
          <Link className="hover:underline" to={baseUrl}>
            {t(category)}
          </Link>
        </p>

        <h2 className="font-bold uppercase">
          <Link className="hover:underline" to={url} state={state}>
            {item.title}
          </Link>
        </h2>

        {item.excerpt ? (
          <p className="text-sm font-medium text-gray-600 line-clamp-3">
            {item.excerpt}
          </p>
        ) : null}

        <p className="text-sm text-gray-600">{item.publishedAt}</p>
      </div>
    </article>
  );
}
